import * as React from 'react';
import { useCallback, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Button, message } from 'antd';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { AssetSuggestion } from '../../types/InvoiceDecisionTypes';
import {
	getDisplayableErrorMessage,
	nullSafeGet,
	nullSafeGetOrElse,
} from '../../utils/DataAccessUtils';
import {
	isThirdPartySupplier,
	isUserAllowedToAccess,
	PERMISSION_NAMES,
} from '../../utils/AuthUtils';
import { getAssetSuggestionFields, getSuggestedAssetFieldUpdates } from '../../utils/InvoiceUtils';
import { assetsRestCrudThunks, patchAsset as patchAssetThunk } from '../../thunks/assets_thunks';
import { associateAssetWithWorkOrder } from '../../thunks/work_orders_thunks';
import useMountEffect from '../../hooks/useMountEffect';
import { withUserType } from '../withUserType';
import { EnrichAssetModal, getAssetLinkCopy, replacesLinkedAssetFor } from './EnrichAssetModal';

// Named as Access Control lists them, so whoever reads the popover can find each one.
const ENRICH_ASSETS_FROM_INVOICES_LABEL = 'Enrich Assets from Invoices';
const EDIT_WORK_ORDERS_LABEL = 'Can Edit Work Orders';
const MODIFY_ASSETS_LABEL = 'Add/Modify Assets';
// Not a permission of this person's: the buyer's company setting, so it says whose it is.
const THIRD_PARTY_ASSOCIATION_LABEL = 'Allow Service Provider to associate assets (client setting)';

interface InvoiceEnrichmentBannerProps {
	assetSuggestion: AssetSuggestion;
	/** The work order's asset when the invoice already carries it hydrated — saves a read. */
	hydratedAsset?: any;
	/** A link suggestion whose asset the work order does not link yet — see isAssetLinkPending. */
	linkPending?: boolean;
	workOrderId?: number;
	/** The invoice's work order as hydrated: its current asset and the buyer's association setting. */
	workOrder?: any;
	userType?: string;
	currentUser?: any;
	companyConfig?: any;
	readAsset?: (id: any) => Promise<any>;
	patchAsset?: (id: any, updates: any) => Promise<any>;
	linkAsset?: (workOrderId: any, assetId: any) => Promise<any>;
}

/**
 * Offers the asset-record updates this invoice implies — a model or serial the invoice printed
 * that the work order's linked asset record is missing or disagrees with.
 *
 * The backend produces these on a clean work-order match and never writes them itself, so this is
 * the only place they can be applied. Everyone sees the offer and can open it; only submitting is
 * gated — on ENRICH_ASSETS_FROM_INVOICES plus the permission the step's write needs (MODIFY_ASSETS
 * to enrich, EDIT_WORK_ORDERS to link). Someone short of either gets a disabled button whose popover
 * names what is missing, so they know whom to ask rather than wondering why nothing is offered.
 *
 * The asset is read up front, not on VIEW, because the suggestion is stored on the invoice forever
 * and the backend has no way to mark it handled. Comparing each suggested value against the live
 * record is what stops an already-applied suggestion being offered again on every visit — see
 * getSuggestedAssetFieldUpdates. What it cannot see is a deliberate decline: SKIP closes the modal
 * but the offer returns next visit, because there is nowhere to record the decision. Fixing that
 * needs the backend to accept an acknowledgement.
 *
 * A link suggestion (`isLinkSuggestion`) adds a step in front: link the suggested asset to the work
 * order, replacing any current link, then offer its fields. Linking is editing the work order, so it
 * needs EDIT_WORK_ORDERS — and, for a third-party supplier, the buyer allowing asset association,
 * the same rule as SupplierAssociateAssetInWorkOrder. The fields only follow a successful link: they
 * describe an asset the work order does not have yet, so patching them without the link would
 * enrich the wrong record's context.
 */
const InvoiceEnrichmentBannerComponent: React.FC<InvoiceEnrichmentBannerProps> = ({
	assetSuggestion,
	hydratedAsset,
	linkPending: initialLinkPending,
	workOrderId,
	workOrder,
	userType,
	currentUser,
	companyConfig,
	readAsset,
	patchAsset,
	linkAsset,
}) => {
	const [modalVisible, setModalVisible] = useState(false);
	const [asset, setAsset] = useState<any>(hydratedAsset || null);
	// Until the asset is known there is nothing to compare against, so the banner stays hidden
	// rather than appearing and then vanishing once the record lands.
	const [assetResolved, setAssetResolved] = useState(!!hydratedAsset);
	const [applying, setApplying] = useState(false);
	// Derived, not copied: when the page feeds the live work order (the work order invoices tab), a
	// link made from another invoice card flips the prop and this card follows. `linkedHere` covers
	// the page that only holds the invoice's snapshot of its work order until it reloads.
	const [linkedHere, setLinkedHere] = useState(false);
	const linkPending = !!initialLinkPending && !linkedHere;
	const [linking, setLinking] = useState(false);

	const assetId = nullSafeGet('assetId', assetSuggestion);
	const hasSuggestion = getAssetSuggestionFields(assetSuggestion).length > 0;

	// What this person is missing to submit each step, by the Access Control label an admin would
	// look for. Empty means allowed.
	const { missingToLink, missingToEnrich } = useMemo(() => {
		const roles = nullSafeGetOrElse('roles', currentUser, []);
		const isAllowed = (permissionName) =>
			isUserAllowedToAccess(userType)(permissionName, companyConfig, roles);
		const missingEnrichFromInvoices = isAllowed(PERMISSION_NAMES.ENRICH_ASSETS_FROM_INVOICES)
			? []
			: [ENRICH_ASSETS_FROM_INVOICES_LABEL];
		// Absent setting (or an unhydrated buyer company) reads as not allowed — the conservative
		// answer for writing to another company's work order.
		const buyerBlocksThirdPartyLink =
			isThirdPartySupplier(currentUser) &&
			!nullSafeGetOrElse(
				'buyerCompany.buyerCompanySettings.config.workOrder.allowAssetAssociationByThirdPartySuppliers',
				workOrder,
				false
			);
		return {
			missingToLink: [
				...missingEnrichFromInvoices,
				...(isAllowed(PERMISSION_NAMES.EDIT_WORK_ORDERS) ? [] : [EDIT_WORK_ORDERS_LABEL]),
				...(buyerBlocksThirdPartyLink ? [THIRD_PARTY_ASSOCIATION_LABEL] : []),
			],
			missingToEnrich: [
				...missingEnrichFromInvoices,
				...(isAllowed(PERMISSION_NAMES.MODIFY_ASSETS) ? [] : [MODIFY_ASSETS_LABEL]),
			],
		};
	}, [userType, currentUser, companyConfig, workOrder]);

	// Without a work order there is nothing to link to, whatever the permissions.
	const showLinkStep = linkPending && !!workOrderId;
	// The link step needs the asset's name even with nothing to patch, so it reads regardless of
	// fields.
	const needsAsset = showLinkStep || (!linkPending && hasSuggestion);

	useMountEffect(() => {
		if (!needsAsset || !assetId || asset || !readAsset) {
			return;
		}
		readAsset(assetId)
			.then((record) => setAsset(record))
			.catch(() => {
				// Show the banner rather than swallow a real suggestion because of a transient read
				// failure. The rows then fall back to the currentValue the suggestion recorded, which
				// may be stale, but ENRICH still works: the patch sends only the chosen fields, so
				// nothing depends on holding a copy of the record.
			})
			.finally(() => setAssetResolved(true));
	});

	const fields = useMemo(
		() => getSuggestedAssetFieldUpdates(assetSuggestion, asset),
		[assetSuggestion, asset]
	);

	// Returns the promise so antd's Popconfirm ActionButton owns the pending state: it keeps the
	// popover open with its own spinner until the write settles, instead of closing the instant it
	// is clicked and leaving the user with no feedback.
	const handleEnrich = useCallback(
		(updates) => {
			setApplying(true);
			// Send only the fields being enriched. This used to read the asset back and PUT the whole
			// entity, because PATCH could not carry these three columns; openwrench-app#2425 added
			// serialNumber, modelNumber and manufacturer to the patch whitelist, so the read-modify-
			// write is gone and with it the window where a detail page left open would silently revert
			// whatever someone else had changed on a field we never meant to touch.
			return patchAsset(assetId, updates)
				.then((updated) => {
					message.success('Asset updated.');
					setModalVisible(false);
					// Keep the applied values locally so the rows filter out immediately, the same way
					// they will on the next load once the record is re-read.
					setAsset(updated && updated.id ? updated : { ...asset, ...updates });
				})
				.catch((error) =>
					message.error(
						getDisplayableErrorMessage(error, 'Could not update the asset. Please try again.')
					)
				)
				.finally(() => setApplying(false));
		},
		[asset, assetId, patchAsset]
	);

	// Whether a fields step follows the link. Not a permission: that step opens either way and gates
	// its own button.
	const hasFieldsToReview = fields.length > 0;

	// Same promise hand-off to the Popconfirm as handleEnrich. On success the modal stays open and
	// moves on to the fields when there are any this person can apply; otherwise the job is done.
	const handleLink = useCallback(() => {
		setLinking(true);
		return linkAsset(workOrderId, assetId)
			.then(() => {
				message.success('Asset linked to the work order.');
				setLinkedHere(true);
				if (!hasFieldsToReview) {
					setModalVisible(false);
				}
			})
			.catch((error) =>
				message.error(
					getDisplayableErrorMessage(error, 'Could not link the asset. Please try again.')
				)
			)
			.finally(() => setLinking(false));
	}, [linkAsset, workOrderId, assetId, hasFieldsToReview]);

	const openModal = () => setModalVisible(true);
	const closeModal = () => setModalVisible(false);

	const linkedAssetId = nullSafeGet('assetId', workOrder) || nullSafeGet('asset.id', workOrder);
	const replacesLinkedAsset = replacesLinkedAssetFor(linkedAssetId, assetId);
	const linkCopy = getAssetLinkCopy(replacesLinkedAsset);
	// Replacing a link drops the work order's current asset, so it reads as a danger alert —
	// the same red icon the modal carries for this case.
	const showsReplaceWarning = linkPending && replacesLinkedAsset;
	const bannerText = linkPending
		? `${linkCopy.bannerTitle} ${linkCopy.subtitle}`
		: 'Enrich Data? We found invoice details that can help update asset information';

	const isVisible = showLinkStep || (!linkPending && hasFieldsToReview);
	if (!assetResolved || !isVisible) {
		return null;
	}

	return (
		// A fragment, not AccessPermissionChecker: that component returns its children unwrapped, so
		// two of them arrive as a keyless array and React warns. The permission is resolved above.
		<>
			{/* mb-s matches the 12px `rowSpacing` the sibling InvoiceDraftBanner carries, so banners on
			    this page space themselves rather than relying on the call site. */}
			<div
				className={`mb-s flex flex-row items-center gap-x-s rounded-sm px-m py-3 ${
					showsReplaceWarning ? 'bg-character-dangerBg' : 'bg-purple-1'
				}`}
			>
				{showsReplaceWarning ? (
					// `!`: antd's `.anticon { color: inherit }` beats a plain Tailwind text-* class.
					<ExclamationCircleFilled className="text-xl !text-character-danger" />
				) : null}
				<div className="bodyregular flex-1 text-character-primary">{bannerText}</div>
				<Button type="primary" onClick={openModal}>
					VIEW
				</Button>
			</div>
			{modalVisible ? (
				<EnrichAssetModal
					visible={modalVisible}
					assetSuggestion={assetSuggestion}
					asset={asset}
					applying={applying}
					onEnrich={handleEnrich}
					linkPending={linkPending}
					linkedAsset={nullSafeGet('asset', workOrder)}
					linkedAssetId={linkedAssetId}
					canEnrich={hasFieldsToReview}
					missingToLink={missingToLink}
					missingToEnrich={missingToEnrich}
					linking={linking}
					onLink={handleLink}
					onCancel={closeModal}
					userType={userType}
				/>
			) : null}
		</>
	);
};

const mapStateToProps = (state) => ({
	currentUser: state.session.currentUser,
	companyConfig: state.company_config.detail,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	readAsset: (id) => dispatch(assetsRestCrudThunks(ownProps.userType).readOne(id)),
	patchAsset: (id, updates) => dispatch(patchAssetThunk(ownProps.userType)(id, updates)),
	linkAsset: (workOrderId, assetId) =>
		dispatch(associateAssetWithWorkOrder(ownProps.userType)({ assetId }, workOrderId)),
});

export const InvoiceEnrichmentBanner = withUserType(
	connect(mapStateToProps, mapDispatchToProps)(InvoiceEnrichmentBannerComponent as any) as any
);

export default InvoiceEnrichmentBanner;
